import React, { useState } from 'react'
import moment from 'moment'

import { translate } from 'locales'

// Components
import { Title, Button } from 'components'

// Images
import pdfIcon from 'assets/icons/pdf.svg'
import noData from 'assets/images/no-data.svg'
import * as S from './styles'

// Interfaces
import { IComponentProps } from './interfaces'

const ImagesAndAttachments: React.FunctionComponent<IComponentProps> = ({
  doctorName,
  exams,
}) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [previewPdf, setPreviewPdf] = useState<string | null>(null)
  const [zoom, setZoom] = useState<number>(1)
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })
  const [imageDimensions, setImageDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 3)) // Max zoom 3x
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 0.5)) // Min zoom 0.5x
  }

  const handleResetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoom((prev) => Math.max(0.5, Math.min(3, prev + delta)))
  }

  const handleImageClick = (imageUrl: string) => {
    setPreviewImage(imageUrl)
    setPreviewPdf(null) // Garantir que PDF não está sendo exibido
    setZoom(1) // Reset zoom when opening new image
    setPosition({ x: 0, y: 0 }) // Reset position
    setImageDimensions(null) // Reset dimensions

    // Load image to get its natural dimensions
    const img = new Image()
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
    img.src = imageUrl
  }

  const handlePdfClick = (pdfUrl: string) => {
    setPreviewPdf(pdfUrl)
    setPreviewImage(null) // Garantir que imagem não está sendo exibida
  }

  const handleDownload = (fileUrl: string, fileName?: string) => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName || `arquivo-${Date.now()}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const closePreview = () => {
    setPreviewImage(null)
    setPreviewPdf(null)
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setImageDimensions(null)
  }

  // Função para gerar URL do Google Docs Viewer
  const getGoogleDocsViewerUrl = (pdfUrl: string) => {
    return `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`
  }

  return (
    <S.SectionContent>
      <span>{moment(new Date()).format('dddd, DD/MM/YYYY')}</span>
      <p>{`${translate('medicalRecord.by')} ${doctorName}`}</p>

      <div className="subsection">
        <Title text={translate('medicalRecord.images')} />

        {exams && exams === null && (
          <S.NoData>
            <img
              src={noData}
              alt="Icone simbolizando que não existe imagens a serem exibidas"
            />
            <span>{translate('medicalRecord.notSendExams')}</span>
          </S.NoData>
        )}

        <S.AttachmentsList>
          {exams &&
            exams !== null &&
            exams?.map((exam: any, index: any) => {
              const isImage = exam.mimetype
              return isImage !== '.pdf' ? (
                <button
                  key={exam.file_key}
                  type="button"
                  className="images"
                  onClick={() => handleImageClick(exam.file_url)}
                >
                  <img src={exam.file_url} alt="Imagem de um exame" />
                  <span>
                    {`${translate('medicalRecord.file')} ${index + 1}`}
                  </span>
                </button>
              ) : (
                <button
                  key={exam.file_key}
                  type="button"
                  className="files"
                  onClick={() => handlePdfClick(exam.file_url)}
                >
                  <img
                    src={pdfIcon}
                    alt="Icone que representa um arquivo PDF"
                  />
                  <span>
                    {`${translate('medicalRecord.file')} ${index + 1}`}
                  </span>
                </button>
              )
            })}
        </S.AttachmentsList>
      </div>

      {/* Modal de pré-visualização de imagem com zoom */}
      {previewImage && (
        <S.ImagePreviewModal onClick={closePreview}>
          <S.ImagePreviewContent onClick={(e) => e.stopPropagation()}>
            <S.ImagePreviewTitle>
              {translate('modal.imagePreview')}
            </S.ImagePreviewTitle>

            {/* Controles de zoom */}
            <S.ZoomControlsContainer>
              <Button
                type="button"
                color="primary"
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                styles={{ marginRight: '5px', padding: '5px 10px' }}
              >
                -
              </Button>
              <S.ZoomPercentage>{Math.round(zoom * 100)}%</S.ZoomPercentage>
              <Button
                type="button"
                color="primary"
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                styles={{ marginLeft: '5px', padding: '5px 10px' }}
              >
                +
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={handleResetZoom}
                styles={{ marginLeft: '10px', padding: '5px 10px' }}
              >
                Reset
              </Button>
            </S.ZoomControlsContainer>

            {/* Container da imagem com zoom e drag */}
            <S.ImageViewerContainer
              {...({
                imageWidth: imageDimensions?.width,
                imageHeight: imageDimensions?.height,
              } as any)}
            >
              <S.ZoomableImage
                src={previewImage}
                alt="Pré-visualização do exame"
                zoom={zoom}
                position={position}
                isDragging={isDragging}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
                draggable={false}
                {...({
                  naturalWidth: imageDimensions?.width,
                  naturalHeight: imageDimensions?.height,
                } as any)}
              />
            </S.ImageViewerContainer>

            <S.ImagePreviewButtons>
              <Button
                type="button"
                color="primary"
                onClick={() =>
                  handleDownload(previewImage, `exame-${Date.now()}.jpg`)
                }
                styles={{ marginRight: '10px' }}
              >
                {translate('modal.download')}
              </Button>
              <Button
                type="button"
                color="link-danger"
                onClick={closePreview}
                styles={{ border: '1px solid #dc3545', color: '#dc3545' }}
              >
                {translate('modal.close')}
              </Button>
            </S.ImagePreviewButtons>
          </S.ImagePreviewContent>
        </S.ImagePreviewModal>
      )}

      {/* Modal de pré-visualização de PDF */}
      {previewPdf && (
        <S.ImagePreviewModal onClick={closePreview}>
          <S.ImagePreviewContent onClick={(e) => e.stopPropagation()}>
            <S.ImagePreviewTitle>Pré-visualização do PDF</S.ImagePreviewTitle>

            {/* Container do PDF */}
            <S.PdfViewerContainer>
              <iframe
                src={getGoogleDocsViewerUrl(previewPdf)}
                title="Pré-visualização do PDF"
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                onError={() => {
                  // Fallback: se o Google Docs Viewer falhar, tenta abrir diretamente
                  const iframe = document.querySelector(
                    'iframe[title="Pré-visualização do PDF"]'
                  ) as HTMLIFrameElement
                  if (iframe) {
                    iframe.src = previewPdf
                  }
                }}
              />
            </S.PdfViewerContainer>

            <S.ImagePreviewButtons>
              <Button
                type="button"
                color="primary"
                onClick={() =>
                  handleDownload(previewPdf, `documento-${Date.now()}.pdf`)
                }
                styles={{ marginRight: '10px' }}
              >
                {translate('modal.download')}
              </Button>
              <Button
                type="button"
                color="link-danger"
                onClick={closePreview}
                styles={{ border: '1px solid #dc3545', color: '#dc3545' }}
              >
                {translate('modal.close')}
              </Button>
            </S.ImagePreviewButtons>
          </S.ImagePreviewContent>
        </S.ImagePreviewModal>
      )}
    </S.SectionContent>
  )
}

export default ImagesAndAttachments
